
'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
// Import usePathname instead of useRouter for initial checks
import { usePathname } from 'next/navigation';
import { login as authLogin, logout as authLogout, isAuthenticated, refreshAccessToken } from '../lib/auth';
import { getCurrentUser } from '@/lib/teamAPI';

interface AuthUser {
  isLoggedIn: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  redirectToLogin: () => void;
  redirectToDashboard: () => void;
}

// Create context with default values that don't depend on browser APIs
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  login: async () => false,
  logout: () => {},
  redirectToLogin: () => {},
  redirectToDashboard: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  
  // Navigation functions that don't use the router directly
  const redirectToLogin = () => {
    if (typeof window !== 'undefined') {
      // Store current path before redirecting
      localStorage.setItem('returnPath', window.location.pathname);
      window.location.href = '/login';
    }
  };
  
  const redirectToDashboard = async () => {
    if (typeof window === 'undefined') return;
    const returnPath = localStorage.getItem('returnPath');
    localStorage.removeItem('returnPath');
    if (returnPath && returnPath !== '/login' && returnPath !== '/') {
      window.location.href = returnPath;
      return;
    }
    try {
      const me = await getCurrentUser();
      window.location.href = me.is_staff ? '/admin' : '/dashboard';
    } catch {
      window.location.href = '/dashboard';
    }
  };

  // Wait for the component to mount on the client side
  useEffect(() => {
    setMounted(true);
  }, []);

  // Check authentication status when mounted on client
  useEffect(() => {
    if (!mounted) return;
    
    const checkAuth = async () => {
      if (isAuthenticated()) {
        // Keep the same object if already logged in. A new `{ isLoggedIn: true }`
        // on every pathname change retriggers CurrentUser/Access loads and
        // unmounts StaffRoute children (hook-count errors on /admin).
        setUser((prev) => prev ?? { isLoggedIn: true });
      } else {
        // Try to refresh token
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          setUser(null);
          // Check if we're on a protected route
          const protectedRoutes = ['/ap-bot', '/lease-abstract', '/reviewer', '/admin'];
          if (protectedRoutes.some(route => pathname?.startsWith(route))) {
            redirectToLogin();
          }
        } else {
          setUser({ isLoggedIn: true });
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [mounted, pathname]);

  // Set up token refresh timer, and refresh again when a background tab wakes up.
  useEffect(() => {
    if (!mounted || !user) return;

    const refresh = () => {
      void refreshAccessToken();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    const intervalId = setInterval(refresh, 4 * 60 * 1000); // 4 minutes
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, mounted]);

  const login = async (username: string, password: string) => {
    if (!mounted) return false;

    setLoading(true);
    setError(null);

    try {
      console.log('AuthContext: Calling authLogin...');
      const success = await authLogin(username, password);
      console.log('AuthContext: authLogin returned:', success);
      console.log('AuthContext: localStorage access_token exists:', !!localStorage.getItem('access_token'));

      if (success) {
        setUser({ isLoggedIn: true });
        console.log('AuthContext: User state set to logged in');
        return true;
      } else {
        setError('Login failed. Please check your credentials.');
        return false;
      }
    } catch (err) {
      console.error('AuthContext: Login error:', err);
      setError('An error occurred during login.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    if (!mounted) return;
    
    authLogout();
    setUser(null);
    redirectToLogin();
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    redirectToLogin,
    redirectToDashboard
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}